/**
 * When a recurring template falls due.
 *
 * A hand port of `Contracts/Recurrence.cs` - one of the two files in this app that duplicate C#
 * logic rather than generating it (`rules.ts` is the other), because `ContractsGen` carries data and
 * not behaviour. The duplication is deliberate and the reason is worth stating: the server generates
 * from this schedule and the editor previews it, so the preview IS the schedule rather than a guess
 * at it - and a preview that disagreed with the generator would be worse than no preview, because
 * it would be believed. `recurrence.test.ts` pins the cases the two must agree on.
 *
 * AN OCCURRENCE IS MIDDAY UTC, always. A record stamped 00:00 UTC on the 1st renders as the last day
 * of the previous month for every reader west of Greenwich - which the expenses ledger shipped and
 * then had to fix, with a day-1 rent template showing rows dated the 31st, the 30th and the 28th.
 * Midday is clear of both edges at every offset the world uses.
 */
import { RecurrenceInterval } from './contracts.gen.js';

/**
 * The most occurrences one pass will ever backfill. A start-date typo of 1990 would otherwise stamp
 * out hundreds of records nobody asked for.
 */
export const MAX_BACKFILL = 120;

export function intervalLabel(interval: RecurrenceInterval): string {
  switch (interval) {
    case RecurrenceInterval.Monthly:
      return 'Monthly';
    case RecurrenceInterval.Quarterly:
      return 'Quarterly';
    case RecurrenceInterval.Yearly:
      return 'Yearly';
    case RecurrenceInterval.Weekly:
      return 'Weekly';
    default:
      return String(interval);
  }
}

/** Midday UTC on a calendar day named in UTC. */
function midday(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
}

function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** The UTC calendar day of `value`, as midday - the C# `.Date` plus the midday rule, in one step. */
function utcDay(value: Date): Date {
  return midday(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

/** Occurrence `n` (0-based) of a schedule, before any start or end check. */
export function occurrence(
  interval: RecurrenceInterval,
  dayOfMonth: number,
  startUtc: Date,
  n: number
): Date {
  if (interval === RecurrenceInterval.Weekly) {
    const start = utcDay(startUtc);
    return new Date(start.getTime() + n * 7 * 86_400_000);
  }

  const months =
    interval === RecurrenceInterval.Quarterly ? 3 : interval === RecurrenceInterval.Yearly ? 12 : 1;
  // Built from (year, month + k, 1) rather than by adding months to a day, so the 31st of January
  // plus one month is the last day of February rather than the 3rd of March.
  const base = new Date(Date.UTC(startUtc.getUTCFullYear(), startUtc.getUTCMonth() + months * n, 1));
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  const day = Math.min(Math.max(dayOfMonth, 1), daysInMonth(year, month));
  return midday(year, month, day);
}

/** Whether `a` falls on the same UTC calendar day as `b`, or an earlier one. */
function sameDayOrBefore(a: Date, b: Date): boolean {
  return utcDay(a).getTime() <= utcDay(b).getTime();
}

/**
 * Every occurrence due by `throughUtc`: on or after the start date, on or before the end date, on or
 * after `skipBeforeUtc` (a restored customer's templates resume rather than backfilling the gap),
 * and at most `MAX_BACKFILL` of them.
 */
export function due(
  interval: RecurrenceInterval,
  dayOfMonth: number,
  startUtc: Date,
  endUtc: Date | null,
  skipBeforeUtc: Date | null,
  throughUtc: Date
): Date[] {
  const out: Date[] = [];
  for (let n = 0; n < MAX_BACKFILL; n++) {
    const when = occurrence(interval, dayOfMonth, startUtc, n);
    if (when.getTime() > throughUtc.getTime()) break;
    if (endUtc && !sameDayOrBefore(when, endUtc)) break;
    // The start month, before the start day.
    if (utcDay(when).getTime() < utcDay(startUtc).getTime()) continue;
    if (skipBeforeUtc && when.getTime() < skipBeforeUtc.getTime()) continue;
    out.push(when);
  }
  return out;
}

/**
 * The next occurrence a generation pass would create: the first one on or after the start and the
 * skip point, on or before the end, whose period key is not already taken. Null once the schedule
 * has run out.
 *
 * A bounded search rather than date arithmetic, as the server does it: a template that has run
 * weekly for a decade is a few hundred cheap iterations, and the cap keeps a corrupt row finite.
 */
export function nextDue(
  interval: RecurrenceInterval,
  dayOfMonth: number,
  startUtc: Date,
  endUtc: Date | null,
  skipBeforeUtc: Date | null,
  templateId: number,
  existingKeys: ReadonlySet<string>
): Date | null {
  for (let n = 0; n < 2000; n++) {
    const when = occurrence(interval, dayOfMonth, startUtc, n);
    if (endUtc && !sameDayOrBefore(when, endUtc)) return null;
    if (utcDay(when).getTime() < utcDay(startUtc).getTime()) continue;
    if (skipBeforeUtc && when.getTime() < skipBeforeUtc.getTime()) continue;
    if (!existingKeys.has(periodKey(templateId, when))) return when;
  }
  return null;
}

/** `yyyy-MM-dd` in UTC. Never a local rendering: a period is a UTC calendar day. */
export function isoDay(value: Date): string {
  const y = value.getUTCFullYear();
  const m = String(value.getUTCMonth() + 1).padStart(2, '0');
  const d = String(value.getUTCDate()).padStart(2, '0');
  return y + '-' + m + '-' + d;
}

/** `{templateId}:{yyyy-MM-dd}` - one key per occurrence, whatever the interval. */
export function periodKey(templateId: number, occurrenceUtc: Date): string {
  return templateId + ':' + isoDay(occurrenceUtc);
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** "September 2026", "Q3 2026", "2026" or "week of 2026-09-14", for a title or a heading. */
export function periodLabel(interval: RecurrenceInterval, occurrenceUtc: Date): string {
  switch (interval) {
    case RecurrenceInterval.Monthly:
      return MONTHS[occurrenceUtc.getUTCMonth()] + ' ' + occurrenceUtc.getUTCFullYear();
    case RecurrenceInterval.Quarterly:
      return 'Q' + (Math.floor(occurrenceUtc.getUTCMonth() / 3) + 1) + ' ' + occurrenceUtc.getUTCFullYear();
    case RecurrenceInterval.Yearly:
      return String(occurrenceUtc.getUTCFullYear());
    case RecurrenceInterval.Weekly:
      return 'week of ' + isoDay(occurrenceUtc);
    default:
      return isoDay(occurrenceUtc);
  }
}

/**
 * The midday-UTC instant a date box means.
 *
 * Every date a human picks in this app is a calendar day, and the server stores each occurrence at
 * midday - so `2026-10-05` typed here and `2026-10-05` generated there are the same instant, with no
 * timezone in between to move it.
 */
export function middayUtcFromIso(text: string | null | undefined): Date | null {
  if (!text) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (month < 0 || month > 11 || day < 1 || day > 31) return null;
  const when = midday(year, month, day);
  // Rejects 2026-02-31, which `Date.UTC` rolls forward into March rather than refusing.
  if (when.getUTCMonth() !== month || when.getUTCDate() !== day) return null;
  return when;
}

/** The UTC calendar day of a stored instant, as `yyyy-MM-dd` - what a date box is filled with. */
export function isoDayOf(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : isoDay(parsed);
}

/** Today as `yyyy-MM-dd`, in the reader's own calendar - what a date box opens on. */
export function todayIso(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return now.getFullYear() + '-' + m + '-' + d;
}
